import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import traplinked_statuses from "../../data/traplinked_statuses.json";
import { useLastServices } from "../../hooks/useLastServices";
import { RootState } from "../../store";
import {
  MonitorTypes,
  creatingMonitorActions,
} from "../../store/monitor-slice";
import { MonitorService } from "../../types/monitor";
import { CardWrapper } from "../CardWrapper/CardWrapper";
import Modal from "../Modal/Modal";
import { MonitorTypeListItem } from "../MonitorTypeListItem/MonitorTypeListItem";
import styles from "../PropertyAreaMonitorsCard/property-area-monitors-card.module.css";

interface Props {
  dataMonitor: MonitorTypes;
  isServiceStarted?: boolean;
  startService: () => void;
  handleStartService?: () => Promise<void>;
}

export const MonitorTypesCard = ({
  dataMonitor,
  isServiceStarted,
  startService,
  handleStartService,
}: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [wasLoading, setWasLoading] = useState(false);
  const [showNoServicesModal, setShowNoServicesModal] = useState(false);
  const [showAlreadyServicedModal, setShowAlreadyServicedModal] =
    useState(false);

  const [pendingMonitor, setPendingMonitor] = useState<MonitorService | null>(
    null
  );

  const servicedMonitorsRedux = useSelector(
    (state: RootState) => state.servicedMonitors
  );

  const appointmentPropAreas = useSelector(
    (state: RootState) =>
      state.appointmentPropertyAreas.appointmentPropertyAreas
  );

  const [selectedMonitor, setSelectedMonitor] = useState<MonitorService | null>(
    null
  );

  const { lastServices, lastServicesIncludes, loading } = useLastServices(
    selectedMonitor?.id ?? undefined,
    6
  );

  const isViewOnly = useSelector((state: RootState) => state.property.viewOnly);

  useEffect(() => {
    if (isViewOnly && selectedMonitor) {
      if (loading && !wasLoading) {
        setWasLoading(true);
      } else if (!loading && wasLoading) {
        if (lastServices.length > 0) {
          navigate(`/events/${lastServices[0].id}/details`, {
            state: {
              monitor: selectedMonitor,
              lastServices: lastServices.slice(1, 7),
              lastServicesIncludes: lastServicesIncludes.slice(1, 7),
              typeOfAppointment:
                lastServicesIncludes[0]?.appointment?.typeOfAppointment,
              showStationary: true,
              monitorTrapType: selectedMonitor.trapType,
            },
          });
        } else {
          setShowNoServicesModal(true);
          setSelectedMonitor(null);
        }
        setWasLoading(false);
      }
    } else {
      setWasLoading(false);
    }
  }, [
    lastServices,
    loading,
    isViewOnly,
    selectedMonitor,
    navigate,
    wasLoading,
  ]);

  const handleCloseModal = () => {
    setShowNoServicesModal(false);
  };

  const handleNavigation = async (monitor: MonitorService) => {
    if (
      !isViewOnly &&
      servicedMonitorsRedux.servicedMonitors?.includes(monitor.id)
    ) {
      setPendingMonitor(monitor);
      setShowAlreadyServicedModal(true);
      return;
    }

    if (isViewOnly) {
      setSelectedMonitor(monitor);
    } else if (isServiceStarted) {
      dispatch(creatingMonitorActions.setMonitorInfoForServices(monitor));
      navigate(`/monitors/status`);
    } else {
      await startService();
    }
  };

  const propertyAreaLabel =
    appointmentPropAreas.find((area) => area.id === dataMonitor.propertyAreaId)
      ?.label ?? dataMonitor.propertyArea;

  const propertyAreaMap =
    appointmentPropAreas.find((area) => area.id === dataMonitor.propertyAreaId)
      ?.map ?? dataMonitor.map;

  const Title = () => (
    <>
      <b>
        {dataMonitor.monitorType} {propertyAreaLabel}{" "}
      </b>
    </>
  );

  const handleAlreadyServicedConfirm = async () => {
    if (!pendingMonitor) return;

    dispatch(creatingMonitorActions.setMonitorInfoForServices(pendingMonitor));
    if (isServiceStarted) {
      navigate(`/monitors/status`);
    } else {
      await handleStartService?.();
      navigate(`/monitors/status`);
    }

    setShowAlreadyServicedModal(false);
    setPendingMonitor(null);
  };

  const handleAlreadyServicedCancel = () => {
    setShowAlreadyServicedModal(false);
    setPendingMonitor(null);
  };

  return (
    <CardWrapper
      title={<Title />}
      color="white"
      showMapIcon={true}
      monitors={dataMonitor.monitors}
      map={propertyAreaMap ?? undefined}
    >
      <Modal
        isOpen={showAlreadyServicedModal}
        message={t("StatusAlreadySet")}
        note="OverwriteStatus"
        onConfirm={handleAlreadyServicedConfirm}
        onCancel={handleAlreadyServicedCancel}
      />

      <Modal
        isOpen={showNoServicesModal}
        message={t("NoData")}
        onCancel={handleCloseModal}
        cancelText="Back"
        closeButtonColor={"#3D5170"}
      />

      {dataMonitor.monitors?.map((monitor: MonitorService, i: number) => {
        const statusColor = traplinked_statuses.find((s) => s.status === monitor?.status)
                ?.color;

        return (
          <MonitorTypeListItem
            key={i}
            onClick={() => handleNavigation(monitor)}
          >
            <div className={styles["list__card--monitortype"]}>
              {!isViewOnly &&
                (servicedMonitorsRedux.servicedMonitors?.includes(
                  monitor.id
                ) ? (
                  <img
                    alt=""
                    src="/img/green-tick.png"
                    style={{ paddingRight: "8px" }}
                  />
                ) : (
                  <img
                    alt=""
                    src="/img/monitor-check.svg"
                    style={{ paddingRight: "8px" }}
                  />
                ))}
              <span className={styles["list__card--monitortype__span"]}>
                {monitor.monitorType} {monitor.isTemporary ? "T" : ""}
                {monitor.position}, {monitor.location}
              </span>
            </div>
            {isViewOnly &&
              (selectedMonitor?.id === monitor.id ? (
                <ClipLoader size={16.5} />
              ) : (
                <div className={styles["list__card--status-container"]}>
                  {monitor.status != null && (
                    <span
                      className={styles["list__card--status"]}
                      style={{ backgroundColor: statusColor }}
                    />
                  )}
                  <img src="/img/info-icon.png" />
                </div>
              ))}
          </MonitorTypeListItem>
        );
      })}
    </CardWrapper>
  );
};
